import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ImageCarousel = ({ items = [] }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  // Responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play carousel (paused when hovering)
  useEffect(() => {
    if (!isAutoPlay || !items || items.length === 0 || isHovering) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = Math.max(0, items.length - itemsPerView);
        return prev < maxIndex ? prev + 1 : 0;
      });
    }, 3000); // Auto-swipe every 3 seconds

    return () => clearInterval(interval);
  }, [isAutoPlay, items, itemsPerView, isHovering, items.length]);

  if (!items || items.length === 0) {
    return null;
  }

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, items.length - itemsPerView);
      return prev < maxIndex ? prev + 1 : 0;
    });
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 2000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, items.length - itemsPerView);
      return prev > 0 ? prev - 1 : maxIndex;
    });
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 2000);
  };

  const handleImageClick = (artwork) => {
    navigate('/gallery');
  };

  const handleViewGallery = () => {
    navigate('/gallery');
  };

  const handleDotClick = (index) => {
    const maxIndex = Math.max(0, items.length - itemsPerView);
    setCurrentIndex(Math.min(index * itemsPerView, maxIndex));
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 2000);
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-4 text-center">
          Featured Collection
        </h2>
        <p className="text-gray-600 text-center mb-8 sm:mb-12">
          Explore our latest additions to the gallery
        </p>

        {/* Main Carousel Container with Arrows on the sides */}
        <div 
          className="group flex items-center gap-3 sm:gap-4 md:gap-6"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Left Arrow */}
          {items.length > itemsPerView && (
            <button
              onClick={prevSlide}
              className="flex-shrink-0 bg-orange-600 hover:bg-orange-700 text-white p-2 sm:p-2.5 md:p-3 rounded-full shadow-lg transition-colors z-20 flex items-center justify-center"
              aria-label="Previous slide"
            >
              <ChevronLeft size={20} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>
          )}

          {/* Carousel Container */}
          <div className="flex-1 relative w-full overflow-hidden">
            {/* Sliding Container - Smooth slide transition */}
            <div 
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(calc(-${currentIndex * 100}% / ${itemsPerView}))`,
              }}
            >
              {items.map((artwork) => (
                <div
                  key={artwork.id}
                  className="flex-shrink-0"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  {/* Padding wrapper for gap */}
                  <div className="p-2 sm:p-3 md:p-4 h-full">
                    {/* Image Container */}
                    <div
                      onClick={() => handleImageClick(artwork)}
                      className="cursor-pointer group/item transition-transform duration-300 hover:scale-105 relative bg-gray-200 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow h-full"
                    >
                      {/* Aspect Ratio Container */}
                      <div className="relative w-full h-0 pb-[100%]">
                        <img
                          src={artwork.imageUrl}
                          alt={artwork.title}
                          className="absolute inset-0 w-full h-full object-cover group-hover/item:opacity-90 transition-opacity"
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/400x400/D4BBAA/333333?text=Image+Not+Found';
                          }}
                        />
                      </div>

                      {/* Overlay on Hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/40 transition-all duration-300 flex flex-col justify-end p-3 sm:p-4 opacity-0 group-hover/item:opacity-100">
                        <h3 className="text-white font-semibold text-sm sm:text-base md:text-lg truncate">{artwork.title}</h3>
                        <p className="text-gray-200 text-xs sm:text-sm">{artwork.artist}</p>
                        <p className="text-orange-400 text-xs sm:text-sm font-semibold mt-2">View in Gallery →</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          {items.length > itemsPerView && (
            <button
              onClick={nextSlide}
              className="flex-shrink-0 bg-orange-600 hover:bg-orange-700 text-white p-2 sm:p-2.5 md:p-3 rounded-full shadow-lg transition-colors z-20 flex items-center justify-center"
              aria-label="Next slide"
            >
              <ChevronRight size={20} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>
          )}
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-8">
          {Array.from({ length: Math.ceil(items.length / itemsPerView) }).map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-2 sm:h-3 rounded-full transition-all ${
                index === Math.floor(currentIndex / itemsPerView)
                  ? 'bg-orange-600 w-6 sm:w-8'
                  : 'bg-gray-300 w-2 sm:w-3 hover:bg-orange-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-8 sm:mt-10">
          <button
            onClick={handleViewGallery}
            className="px-6 sm:px-8 py-2 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white text-sm sm:text-base font-semibold rounded-lg shadow-lg transition-colors"
          >
            View Full Gallery
          </button>
        </div>
      </div>
    </section>
  );
};

export default ImageCarousel;